import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Chart, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import styles from './ControlChart.module.css';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Import the DatePicker CSS
import { useNavigate } from 'react-router-dom';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import {
    calculateUclLcl,
    fetchAteSwVersions,
    fetchControlChartData,
    fetchControlChartStepNumbers,
    fetchFilenames,
    fetchTestspecs,
} from './controlChart/controlChartApi';
import {
    calculateCapabilityMetrics,
    buildControlNormalDistributionChartData,
    buildControlNormalDistributionOptions,
    findStepData,
    getQuickDateRange,
    normalizeControlChartData,
    sortFilenamesByDate,
} from './controlChart/controlChartData';
import {
    exportChartAsPNG,
    exportStepTableAsCSV,
    exportStepTableAsPDF,
} from './controlChart/controlChartExport';
const formatCapabilityValue = (value, digits = 3) => (Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : 'N/A');

const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(parsed);
};

const toFiniteNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const buildCriticalityReason = ({
    outOfSpecLow,
    outOfSpecHigh,
    outOfControlLow,
    outOfControlHigh,
    nearSpec,
    nearControl,
}) => {
    const reasons = [];
    if (outOfSpecLow) reasons.push('Below LSL');
    if (outOfSpecHigh) reasons.push('Above USL');
    if (outOfControlLow) reasons.push('Below LCL');
    if (outOfControlHigh) reasons.push('Above UCL');
    if (!reasons.length && nearSpec) reasons.push('Near specification limits');
    if (!reasons.length && nearControl) reasons.push('Near control limits');
    return reasons.length ? reasons.join(' + ') : 'Within limits';
};

const annotateControlRows = (rows = [], limits = {}) => rows.map((row, index) => {
    const value = toFiniteNumber(row.measureValue);
    const mean = toFiniteNumber(limits.mean);
    const lowerControlLimit = toFiniteNumber(limits.lowerControlLimit);
    const upperControlLimit = toFiniteNumber(limits.upperControlLimit);
    const lowerSpecLimit = toFiniteNumber(limits.lowerSpecLimit ?? row.limitLow);
    const upperSpecLimit = toFiniteNumber(limits.upperSpecLimit ?? row.limitHigh);
    const specSpan = Number.isFinite(lowerSpecLimit) && Number.isFinite(upperSpecLimit)
        ? Math.abs(upperSpecLimit - lowerSpecLimit)
        : null;
    const controlSpan = Number.isFinite(lowerControlLimit) && Number.isFinite(upperControlLimit)
        ? Math.abs(upperControlLimit - lowerControlLimit)
        : null;
    const nearestSpecDistance = Number.isFinite(value)
        ? Math.min(
            Number.isFinite(lowerSpecLimit) ? Math.abs(value - lowerSpecLimit) : Number.POSITIVE_INFINITY,
            Number.isFinite(upperSpecLimit) ? Math.abs(upperSpecLimit - value) : Number.POSITIVE_INFINITY
        )
        : Number.POSITIVE_INFINITY;
    const nearestControlDistance = Number.isFinite(value)
        ? Math.min(
            Number.isFinite(lowerControlLimit) ? Math.abs(value - lowerControlLimit) : Number.POSITIVE_INFINITY,
            Number.isFinite(upperControlLimit) ? Math.abs(upperControlLimit - value) : Number.POSITIVE_INFINITY
        )
        : Number.POSITIVE_INFINITY;

    const outOfSpecLow = Number.isFinite(value) && Number.isFinite(lowerSpecLimit) && value < lowerSpecLimit;
    const outOfSpecHigh = Number.isFinite(value) && Number.isFinite(upperSpecLimit) && value > upperSpecLimit;
    const outOfControlLow = Number.isFinite(value) && Number.isFinite(lowerControlLimit) && value < lowerControlLimit;
    const outOfControlHigh = Number.isFinite(value) && Number.isFinite(upperControlLimit) && value > upperControlLimit;
    const nearSpec = !outOfSpecLow && !outOfSpecHigh && Number.isFinite(specSpan) && specSpan > 0 && nearestSpecDistance <= (specSpan * 0.1);
    const nearControl = !outOfControlLow && !outOfControlHigh && Number.isFinite(controlSpan) && controlSpan > 0 && nearestControlDistance <= (controlSpan * 0.08);
    const isCritical = outOfSpecLow || outOfSpecHigh || outOfControlLow || outOfControlHigh;
    const isWarning = !isCritical && (nearSpec || nearControl);
    const criticalityLabel = isCritical ? 'Critical' : isWarning ? 'Warning' : 'Stable';

    return {
        ...row,
        observation: index + 1,
        measuredAtLabel: formatDateTime(row.testStarted),
        value,
        deltaFromCl: Number.isFinite(value) && Number.isFinite(mean) ? Number((value - mean).toFixed(4)) : null,
        effectiveLsl: lowerSpecLimit,
        effectiveLcl: lowerControlLimit,
        effectiveUcl: upperControlLimit,
        effectiveUsl: upperSpecLimit,
        criticalityLabel,
        criticalityReason: buildCriticalityReason({
            outOfSpecLow,
            outOfSpecHigh,
            outOfControlLow,
            outOfControlHigh,
            nearSpec,
            nearControl,
        }),
        isCritical,
        isWarning,
        isOutOfSpec: outOfSpecLow || outOfSpecHigh,
        isOutOfControl: outOfControlLow || outOfControlHigh,
    };
});

const buildCriticalPointDataset = (rows = [], label = 'Critical points') => ({
    label,
    data: rows.map((row) => (row.isCritical ? row.value : null)),
    borderColor: 'transparent',
    backgroundColor: rows.map((row) => (
        row.isOutOfSpec
            ? '#dc2626'
            : row.isOutOfControl
                ? '#f97316'
                : 'transparent'
    )),
    pointBorderColor: rows.map((row) => (
        row.isOutOfSpec
            ? '#991b1b'
            : row.isOutOfControl
                ? '#9a3412'
                : 'transparent'
    )),
    pointRadius: rows.map((row) => (row.isCritical ? 5 : 0)),
    pointHoverRadius: rows.map((row) => (row.isCritical ? 7 : 0)),
    pointBorderWidth: rows.map((row) => (row.isCritical ? 2 : 0)),
    showLine: false,
    order: -1,
});

const buildCapabilityMetricCards = (metrics) => [
    { label: 'LSL', value: formatCapabilityValue(metrics?.lsl, 4) },
    { label: 'USL', value: formatCapabilityValue(metrics?.usl, 4) },
    { label: 'Mean', value: formatCapabilityValue(metrics?.mean, 4) },
    { label: 'Sigma within', value: formatCapabilityValue(metrics?.sigmaWithin, 4) },
    { label: 'Sigma overall', value: formatCapabilityValue(metrics?.sigmaOverall, 4) },
    { label: 'Cp', value: formatCapabilityValue(metrics?.cp) },
    { label: 'Cpu', value: formatCapabilityValue(metrics?.cpu) },
    { label: 'Cpl', value: formatCapabilityValue(metrics?.cpl) },
    { label: 'Cpk', value: formatCapabilityValue(metrics?.cpk) },
    { label: 'Pp', value: formatCapabilityValue(metrics?.pp) },
    { label: 'Ppu', value: formatCapabilityValue(metrics?.ppu) },
    { label: 'Ppl', value: formatCapabilityValue(metrics?.ppl) },
    { label: 'Ppk', value: formatCapabilityValue(metrics?.ppk) },
    { label: 'k', value: formatCapabilityValue(metrics?.k) },
    { label: 'Z bench', value: formatCapabilityValue(metrics?.zBench) },
];

const CONTROL_MODAL_SIZES = [
    { key: 'medium', label: 'Medio' },
    { key: 'large', label: 'Grande' },
    { key: 'full', label: 'Schermo intero' },
];



ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

export const CONTROL_CHART_RESULTS_STORAGE_KEY = 'smarti:last-control-chart-results';

const persistControlChartResults = (payload) => {
    try {
        window.localStorage.setItem(CONTROL_CHART_RESULTS_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        console.warn('Could not persist control-chart results:', error);
    }
};

const ControlChart = ({ embedded = false }) => {
    const [chartData, setChartData] = useState([]);
    const [chartDataAll, setChartDataAll] = useState([]);
    const [stepNumbers, setStepNumbers] = useState([]);
    const [stepNumberLoading, setStepNumberLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(false);
    const [selectedStepNumber, setSelectedStepNumber] = useState('');
    const [pendingStepNumber, setPendingStepNumber] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const normalChartRef = useRef(null);
    const individualChartRef = useRef(null);
    const individualAllChartRef = useRef(null);
    const mrChartRef = useRef(null);
    const normalChartExportRef = useRef(null);
    const individualChartExportRef = useRef(null);
    const individualAllChartExportRef = useRef(null);
    const mrChartExportRef = useRef(null);
    const [lsl, setLsl] = useState('');
    const [usl, setUsl] = useState('');
    const [ucl, setUcl] = useState('');
    const [lcl, setLcl] = useState('');
    // Temporary values for inputs to apply limits
    const [tempLsl, setTempLsl] = useState('');
    const [tempUsl, setTempUsl] = useState('');
    const [tempUcl, setTempUcl] = useState('');
    const [tempLcl, setTempLcl] = useState('');
    // Default LSL and USL values, set initially from API data
    const [defaultLsl, setDefaultLsl] = useState('');
    const [defaultUsl, setDefaultUsl] = useState('');
    const [defaultUcl, setDefaultUcl] = useState('');
    const [defaultLcl, setDefaultLcl] = useState('');
    // Validation error message
    const [errorMessage, setErrorMessage] = useState('');
    // Date filter states
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    // Temporary states for date input fields
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);
    // States for serial numbers and AteSwVersion
    const [selectedAteSwVersion, setSelectedAteSwVersion] = useState('');
    const [ateSwVersions, setAteSwVersions] = useState([]); // Store all distinct AteSwVersion values
    // States for serial numbers and Testspec
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [testspecs, setTestspecs] = useState([]); // Store all distinct Testspec values
    const [serialFilter, setSerialFilter] = useState('');
    const [stationFilter, setStationFilter] = useState('');
    const [filenameFilter, setFilenameFilter] = useState('');
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const openHelp = () => setIsHelpOpen(true);
    const closeHelp = () => setIsHelpOpen(false);
    // calculator
    const [filenames, setFilenames] = useState([]);
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const navigate = useNavigate();
    const [modalSize, setModalSize] = useState('large');
    const [openChartDetails, setOpenChartDetails] = useState({});
    const [includeDetailsInExport, setIncludeDetailsInExport] = useState({});
    const [tableSort, setTableSort] = useState({ key: 'criticalityLabel', direction: 'desc' });

    const [sortOrder, setSortOrder] = useState('newest'); // Track sort order
    const [sortedFilenames, setSortedFilenames] = useState([]); // Sorted filenames
    const [calculatorSearch, setCalculatorSearch] = useState('');
    const [calculatorStatusFilter, setCalculatorStatusFilter] = useState('');
    const [calculatorAteFilter, setCalculatorAteFilter] = useState('');
    const [calculatorTestspecFilter, setCalculatorTestspecFilter] = useState('');
    const [calculatorDateFrom, setCalculatorDateFrom] = useState('');
    const [calculatorDateTo, setCalculatorDateTo] = useState('');



    // Sort files whenever filenames or sortOrder changes
    useEffect(() => {
        setSortedFilenames(sortFilenamesByDate(filenames, sortOrder));
    }, [filenames, sortOrder]);

    const calculatorStatuses = useMemo(
        () => [...new Set(filenames.map((file) => String(file.uutStatus || '').trim()).filter(Boolean))].sort(),
        [filenames]
    );
    const calculatorAteVersions = useMemo(
        () => [...new Set(filenames.map((file) => String(file.AteSwVersion || '').trim()).filter(Boolean))].sort(),
        [filenames]
    );
    const calculatorTestspecs = useMemo(
        () => [...new Set(filenames.map((file) => String(file.Testspec || '').trim()).filter(Boolean))].sort(),
        [filenames]
    );
    const visibleCalculatorFiles = useMemo(() => {
        const searchTerm = calculatorSearch.trim().toLowerCase();
        return sortedFilenames.filter((file) => {
            const haystack = [
                file.filename,
                file.serialNumber,
                file.AteSwVersion,
                file.Testspec,
                file.uutStatus,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            const searchMatches = !searchTerm || haystack.includes(searchTerm);
            const statusMatches = !calculatorStatusFilter || String(file.uutStatus || '') === calculatorStatusFilter;
            const ateMatches = !calculatorAteFilter || String(file.AteSwVersion || '') === calculatorAteFilter;
            const testspecMatches = !calculatorTestspecFilter || String(file.Testspec || '') === calculatorTestspecFilter;
            const fileDate = String(file.testStarted || '').slice(0, 10).replace(/\//g, '-');
            const dateFromMatches = !calculatorDateFrom || (fileDate && fileDate >= calculatorDateFrom);
            const dateToMatches = !calculatorDateTo || (fileDate && fileDate <= calculatorDateTo);
            return searchMatches && statusMatches && ateMatches && testspecMatches && dateFromMatches && dateToMatches;
        });
    }, [
        calculatorAteFilter,
        calculatorDateFrom,
        calculatorDateTo,
        calculatorSearch,
        calculatorStatusFilter,
        calculatorTestspecFilter,
        sortedFilenames,
    ]);

    useEffect(() => {
        // Simulate a 7 seconds loading time
        setTimeout(() => {
            setLoading(false);  // Hide loading animation after 3 seconds
        }, 3000);
    }, []);

    const openModal = (chartType) => {
        setModalContent(chartType);
        setModalSize('large');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalContent(null);
        setModalSize('large');
    };
    const toggleChartDetails = (chartKey) => {
        setOpenChartDetails((current) => ({
            ...current,
            [chartKey]: !current[chartKey],
        }));
    };
    const toggleIncludeDetailsInExport = (chartKey) => {
        setIncludeDetailsInExport((current) => ({
            ...current,
            [chartKey]: !current[chartKey],
        }));
    };
    const toggleTableSort = (columnKey) => {
        setTableSort((current) => {
            if (current.key === columnKey) {
                return {
                    key: columnKey,
                    direction: current.direction === 'desc' ? 'asc' : 'desc',
                };
            }
            return {
                key: columnKey,
                direction: columnKey === 'criticalityLabel' ? 'desc' : 'asc',
            };
        });
    };

    const exportTableAsCSV = () => {
        exportStepTableAsCSV({
            title: `Control chart details for step ${selectedStepNumber}`,
            columns: tableColumns,
            rows: tableRows,
        });
    };

    const exportTableAsPDF = () => {
        exportStepTableAsPDF({
            title: `Control chart details for step ${selectedStepNumber}`,
            columns: tableColumns,
            rows: tableRows,
        });
    };

    useEffect(() => {
        const loadAteSwVersions = async () => {
            try {
                setAteSwVersions(await fetchAteSwVersions());
            } catch (error) {
                console.error('Error fetching AteSwVersion data:', error);
            }
        };
        loadAteSwVersions();
    }, []);

    useEffect(() => {
        const loadTestspecs = async () => {
            try {
                setTestspecs(await fetchTestspecs());
            } catch (error) {
                console.error('Error fetching Testspec data:', error);
            }
        };
        loadTestspecs();
    }, []);

    const commonControlParams = useMemo(() => ({
        lsl: lsl || undefined,
        usl: usl || undefined,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
        ateSwVersion: selectedAteSwVersion || undefined,
        Testspec: selectedTestspec || undefined,
        serialNumber: serialFilter.trim() || undefined,
        stationName: stationFilter.trim() || undefined,
        filename: filenameFilter.trim() || undefined,
        selectedFamilies: selectedFamilies.join(',') || undefined,
    }), [filenameFilter, lsl, serialFilter, selectedAteSwVersion, selectedFamilies, selectedTestspec, startDate, stationFilter, usl, endDate]);

    useEffect(() => {
        const loadStepNumbers = async () => {
            setStepNumberLoading(true);
            try {
                const steps = await fetchControlChartStepNumbers(commonControlParams);
                const normalizedSteps = (steps || [])
                    .map((step) => {
                        if (typeof step === 'string') {
                            return { stepNumber: step.trim(), stepName: '', count: 0 };
                        }

                        return {
                            stepNumber: String(step.stepNumber || '').trim(),
                            stepName: step.stepName || '',
                            count: Number(step.count) || 0,
                        };
                    })
                    .filter((step) => step.stepNumber);

                setStepNumbers(normalizedSteps);
                setPendingStepNumber((current) => {
                    if (current && normalizedSteps.some((step) => step.stepNumber === String(current))) return current;
                    return normalizedSteps[0]?.stepNumber || '';
                });
                setSelectedStepNumber((current) => {
                    if (current && normalizedSteps.some((step) => step.stepNumber === String(current))) return current;
                    return '';
                });
            } catch (error) {
                console.error('Error fetching control chart step numbers:', error);
                try {
                    const fallbackData = normalizeControlChartData(await fetchControlChartData(commonControlParams));
                    const fallbackSteps = fallbackData
                        .map((step) => ({
                            stepNumber: String(step.stepNumber).trim(),
                            stepName: step.stepName || '',
                            count: step.values?.length || 0,
                        }))
                        .filter((step) => step.stepNumber);

                    setStepNumbers(fallbackSteps);
                    setPendingStepNumber((current) => {
                        if (current && fallbackSteps.some((step) => step.stepNumber === String(current))) return current;
                        return fallbackSteps[0]?.stepNumber || '';
                    });
                    setSelectedStepNumber((current) => {
                        if (current && fallbackSteps.some((step) => step.stepNumber === String(current))) return current;
                        return '';
                    });
                } catch (fallbackError) {
                    console.error('Error fetching fallback control chart data:', fallbackError);
                    setStepNumbers([]);
                    setPendingStepNumber('');
                    setSelectedStepNumber('');
                }
            } finally {
                setStepNumberLoading(false);
            }
        };

        loadStepNumbers();
    }, [commonControlParams]);

    useEffect(() => {
        const loadControlChartData = async () => {
            if (!selectedStepNumber) {
                setChartData([]);
                setChartDataAll([]);
                return;
            }

            try {
                setChartLoading(true);
                const params = {
                    ...commonControlParams,
                    stepNumber: selectedStepNumber,
                };

                const [passOnlyData, passFailData] = await Promise.all([
                    fetchControlChartData(params),
                    fetchControlChartData({
                        ...params,
                        includeFail: true,
                    }),
                ]);

                const normalizedPassOnlyData = normalizeControlChartData(passOnlyData);
                const normalizedPassFailData = normalizeControlChartData(passFailData);

                setChartData(normalizedPassOnlyData);
                setChartDataAll(normalizedPassFailData);
            } catch (error) {
                console.error('Error fetching control chart data:', error);
                setChartData([]);
                setChartDataAll([]);
            } finally {
                setChartLoading(false);
            }
        };

        loadControlChartData();
    }, [commonControlParams, selectedStepNumber]);

    useEffect(() => {
        if (chartData.length > 0) {
            const firstStep = chartData[0];
            setDefaultLsl(firstStep.lsl ?? '');
            setDefaultUsl(firstStep.usl ?? '');
            setDefaultUcl(firstStep.individualChart?.ucl ?? '');
            setDefaultLcl(firstStep.individualChart?.lcl ?? '');
            setTempLsl(firstStep.lsl ?? '');
            setTempUsl(firstStep.usl ?? '');
            setTempUcl(firstStep.individualChart?.ucl ?? '');
            setTempLcl(firstStep.individualChart?.lcl ?? '');
        }
    }, [chartData]);


    const handleApplyLimits = () => {
        setLsl(tempLsl);
        setUsl(tempUsl);
        setUcl(tempUcl);
        setLcl(tempLcl);
        setSelectedAteSwVersion(selectedAteSwVersion);
        setSelectedTestspec(selectedTestspec);
    };


    const handleStepNumberChange = (e) => {
        const value = e.target.value;

        setPendingStepNumber(value);

        if (value === '') {
            setDefaultLsl('');
            setDefaultUsl('');
            setTempLsl('');
            setTempUsl('');
            return;
        }

        const stepData = chartData.find((step) => String(step.stepNumber) === value);

        if (stepData) {
            setDefaultLsl(stepData.lsl ?? '');
            setDefaultUsl(stepData.usl ?? '');
            setDefaultUcl(stepData.individualChart?.ucl ?? '');
            setDefaultLcl(stepData.individualChart?.lcl ?? '');
            setTempLsl(stepData.lsl ?? '');
            setTempUsl(stepData.usl ?? '');
            setTempUcl(stepData.individualChart?.ucl ?? '');
            setTempLcl(stepData.individualChart?.lcl ?? '');
        } else {
            setDefaultLsl('');
            setDefaultUsl('');
            setDefaultUcl('');
            setDefaultLcl('');
            setTempLsl('');
            setTempUsl('');
            setTempUcl('');
            setTempLcl('');
        }
    };

    const handleApplyStepNumber = () => {
        if (!pendingStepNumber) {
            setErrorMessage('No step number selected. Please select a step number.');
            return;
        }
        setErrorMessage('');
        setSelectedStepNumber(pendingStepNumber);
    };


    const handleApplyDateFilter = () => {
        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
    };

    const handleResetDateFilter = () => {
        // Reset temporary date states and main date states to null or default
        setTempStartDate(null);
        setTempEndDate(null);
        setStartDate(null);
        setEndDate(null);
    };

    const handleResetLimits = () => {
        // Reset both the LSL and USL states and temporary input values
        console.log("Resetting to default LSL and USL:", defaultLsl, defaultUsl);
        setLsl(defaultLsl);
        setUsl(defaultUsl);
        setUcl(defaultUcl);
        setLcl(defaultLcl);
        setTempLsl(defaultLsl);
        setTempUsl(defaultUsl);
        setTempUcl(defaultUcl);
        setTempLcl(defaultLcl);
        setSelectedAteSwVersion('');
        setSelectedTestspec('');
        setSerialFilter('');
        setStationFilter('');
        setFilenameFilter('');
        setSelectedFamilies([]);
    };

    // Validate LSL and USL inputs
    useEffect(() => {
        if (tempLsl && tempUsl && parseFloat(tempLsl) > parseFloat(tempUsl)) {
            setErrorMessage('LSL cannot be greater than USL.');
        } else {
            setErrorMessage('');
        }
    }, [tempLsl, tempUsl]);




    //calculator
    useEffect(() => {
        const loadFilenames = async () => {
            try {
                setFilenames(await fetchFilenames());
            } catch (error) {
                console.error('Error fetching filenames:', error);
            }
        };
        loadFilenames();
    }, []);



    // Find the data for the selected step number
    const selectedData = findStepData(chartData, selectedStepNumber);
    const selectedDataAll = findStepData(chartDataAll, selectedStepNumber);
    const resolvedLsl = lsl !== '' ? Number(lsl) : toFiniteNumber(selectedData?.lsl);
    const resolvedUsl = usl !== '' ? Number(usl) : toFiniteNumber(selectedData?.usl);
    const resolvedUcl = ucl !== '' ? Number(ucl) : toFiniteNumber(selectedData?.individualChart?.ucl);
    const resolvedLcl = lcl !== '' ? Number(lcl) : toFiniteNumber(selectedData?.individualChart?.lcl);
    const resolvedAllUcl = ucl !== '' ? Number(ucl) : toFiniteNumber(selectedDataAll?.individualChart?.ucl);
    const resolvedAllLcl = lcl !== '' ? Number(lcl) : toFiniteNumber(selectedDataAll?.individualChart?.lcl);
    const capabilityPassOnly = useMemo(
        () => calculateCapabilityMetrics({
            values: selectedData?.values || [],
            lsl: resolvedLsl,
            usl: resolvedUsl,
        }),
        [resolvedLsl, resolvedUsl, selectedData]
    );
    const capabilityPassFail = useMemo(
        () => calculateCapabilityMetrics({
            values: selectedDataAll?.values || [],
            lsl: resolvedLsl,
            usl: resolvedUsl,
        }),
        [resolvedLsl, resolvedUsl, selectedDataAll]
    );
    const capabilityPassOnlyCards = useMemo(
        () => buildCapabilityMetricCards(capabilityPassOnly),
        [capabilityPassOnly]
    );
    const capabilityPassFailCards = useMemo(
        () => buildCapabilityMetricCards(capabilityPassFail),
        [capabilityPassFail]
    );
    const normalDistributionChartData = useMemo(
        () => buildControlNormalDistributionChartData(selectedData),
        [selectedData]
    );
    const normalDistributionChartOptions = useMemo(
        () => buildControlNormalDistributionOptions(normalDistributionChartData),
        [normalDistributionChartData]
    );
    const pendingStepOption = stepNumbers.find((step) => step.stepNumber === pendingStepNumber);
    const selectedStepOption = stepNumbers.find((step) => step.stepNumber === selectedStepNumber);
    const modalSizeClass = modalSize === 'full'
        ? styles.modalSizeFull
        : modalSize === 'medium'
            ? styles.modalSizeMedium
            : styles.modalSizeLarge;
    const modalChartStageClass = modalSize === 'full'
        ? styles.modalChartStageFull
        : modalSize === 'medium'
            ? styles.modalChartStageMedium
            : styles.modalChartStageLarge;
    const passOnlyDetailCards = capabilityPassOnlyCards;
    const passFailDetailCards = capabilityPassFailCards;
    const normalDetailCards = capabilityPassOnlyCards;
    const mrDetailCards = [
        { label: 'Samples', value: selectedData?.values?.length || 0 },
        { label: 'MR bar', value: formatCapabilityValue(selectedData?.mrChart?.mrBar, 4) },
        { label: 'MR UCL', value: formatCapabilityValue(selectedData?.mrChart?.ucl, 4) },
        { label: 'Mean', value: formatCapabilityValue(capabilityPassOnly?.mean, 4) },
        { label: 'Sigma within', value: formatCapabilityValue(capabilityPassOnly?.sigmaWithin, 4) },
        { label: 'Sigma overall', value: formatCapabilityValue(capabilityPassOnly?.sigmaOverall, 4) },
        { label: 'Cp', value: formatCapabilityValue(capabilityPassOnly?.cp) },
        { label: 'Cpk', value: formatCapabilityValue(capabilityPassOnly?.cpk) },
        { label: 'Pp', value: formatCapabilityValue(capabilityPassOnly?.pp) },
        { label: 'Ppk', value: formatCapabilityValue(capabilityPassOnly?.ppk) },
    ];
    const passOnlyAnnotatedRows = useMemo(() => annotateControlRows(selectedData?.rowDetails || [], {
        mean: selectedData?.individualChart?.xBar,
        lowerControlLimit: resolvedLcl,
        upperControlLimit: resolvedUcl,
        lowerSpecLimit: resolvedLsl,
        upperSpecLimit: resolvedUsl,
    }), [resolvedLcl, resolvedLsl, resolvedUcl, resolvedUsl, selectedData]);
    const passFailAnnotatedRows = useMemo(() => annotateControlRows(selectedDataAll?.rowDetails || [], {
        mean: selectedDataAll?.individualChart?.xBar,
        lowerControlLimit: resolvedAllLcl,
        upperControlLimit: resolvedAllUcl,
        lowerSpecLimit: resolvedLsl,
        upperSpecLimit: resolvedUsl,
    }), [resolvedAllLcl, resolvedAllUcl, resolvedLsl, resolvedUsl, selectedDataAll]);
    const tableRows = useMemo(() => {
        const baseRows = passFailAnnotatedRows.length ? passFailAnnotatedRows : passOnlyAnnotatedRows;
        const rows = [...baseRows];
        const rank = { Critical: 3, Warning: 2, Stable: 1 };
        rows.sort((left, right) => {
            let leftValue = left?.[tableSort.key];
            let rightValue = right?.[tableSort.key];

            if (tableSort.key === 'criticalityLabel') {
                leftValue = rank[leftValue] ?? 0;
                rightValue = rank[rightValue] ?? 0;
            }

            const leftNumber = Number(leftValue);
            const rightNumber = Number(rightValue);
            let comparison = 0;
            if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftValue !== '' && rightValue !== '') {
                comparison = leftNumber - rightNumber;
            } else {
                comparison = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, { numeric: true, sensitivity: 'base' });
            }
            if (comparison === 0) {
                comparison = String(left.serialNumber ?? '').localeCompare(String(right.serialNumber ?? ''), undefined, { numeric: true, sensitivity: 'base' });
            }
            return tableSort.direction === 'asc' ? comparison : -comparison;
        });
        return rows;
    }, [passFailAnnotatedRows, passOnlyAnnotatedRows, tableSort]);
    const tableColumns = useMemo(() => ([
        { key: 'criticalityLabel', label: 'Criticality' },
        { key: 'criticalityReason', label: 'Reason' },
        { key: 'serialNumber', label: 'Serial Number' },
        { key: 'stationName', label: 'Station Name' },
        { key: 'observation', label: 'Observation' },
        { key: 'measuredAtLabel', label: 'Measured At' },
        { key: 'uutStatus', label: 'UUT Status' },
        { key: 'stepResult', label: 'Step Result' },
        { key: 'value', label: 'Value', format: (value) => formatCapabilityValue(value, 4) },
        { key: 'deltaFromCl', label: 'Delta from CL', format: (value) => formatCapabilityValue(value, 4) },
        { key: 'effectiveLsl', label: 'LSL', format: (value) => formatCapabilityValue(value, 4) },
        { key: 'effectiveLcl', label: 'LCL', format: (value) => formatCapabilityValue(value, 4) },
        { key: 'effectiveUcl', label: 'UCL', format: (value) => formatCapabilityValue(value, 4) },
        { key: 'effectiveUsl', label: 'USL', format: (value) => formatCapabilityValue(value, 4) },
        { key: 'filename', label: 'Source File' },
        { key: 'AteSwVersion', label: 'ATE SW Version' },
        { key: 'Testspec', label: 'Testspec' },
    ]), []);
    const passOnlyChartData = useMemo(() => {
        if (!selectedData) return null;
        return {
            labels: selectedData.serialNumbers,
            datasets: [
                {
                    label: 'Individual values',
                    data: selectedData.values,
                    borderColor: 'blue',
                    backgroundColor: 'blue',
                    pointRadius: 3,
                    fill: false,
                },
                {
                    label: 'CL',
                    data: Array(selectedData.values.length).fill(selectedData.individualChart?.xBar || null),
                    borderColor: 'green',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'UCL',
                    data: Array(selectedData.values.length).fill(resolvedUcl),
                    borderColor: 'red',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'LCL',
                    data: Array(selectedData.values.length).fill(resolvedLcl),
                    borderColor: 'red',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'LSL',
                    data: Array(selectedData.values.length).fill(resolvedLsl),
                    borderColor: 'purple',
                    borderDash: [10, 5],
                    pointRadius: 0,
                },
                {
                    label: 'USL',
                    data: Array(selectedData.values.length).fill(resolvedUsl),
                    borderColor: 'purple',
                    borderDash: [10, 5],
                    pointRadius: 0,
                },
                buildCriticalPointDataset(passOnlyAnnotatedRows),
            ],
        };
    }, [passOnlyAnnotatedRows, resolvedLcl, resolvedLsl, resolvedUcl, resolvedUsl, selectedData]);
    const passFailChartData = useMemo(() => {
        if (!selectedDataAll) return null;
        return {
            labels: selectedDataAll.serialNumbers,
            datasets: [
                {
                    label: 'Individual values (PASS + FAIL)',
                    data: selectedDataAll.values,
                    borderColor: 'orange',
                    backgroundColor: 'orange',
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    borderWidth: 3,
                    fill: false,
                },
                {
                    label: 'CL',
                    data: Array(selectedDataAll.values.length).fill(selectedDataAll.individualChart?.xBar || null),
                    borderColor: 'green',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'UCL',
                    data: Array(selectedDataAll.values.length).fill(resolvedAllUcl),
                    borderColor: 'red',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'LCL',
                    data: Array(selectedDataAll.values.length).fill(resolvedAllLcl),
                    borderColor: 'red',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'LSL',
                    data: Array(selectedDataAll.values.length).fill(resolvedLsl),
                    borderColor: 'purple',
                    borderDash: [10, 5],
                    pointRadius: 0,
                },
                {
                    label: 'USL',
                    data: Array(selectedDataAll.values.length).fill(resolvedUsl),
                    borderColor: 'purple',
                    borderDash: [10, 5],
                    pointRadius: 0,
                },
                buildCriticalPointDataset(passFailAnnotatedRows),
            ],
        };
    }, [passFailAnnotatedRows, resolvedAllLcl, resolvedAllUcl, resolvedLsl, resolvedUsl, selectedDataAll]);
    const mrChartData = useMemo(() => {
        if (!selectedData) return null;
        return {
            labels: selectedData.serialNumbers.slice(1),
            datasets: [
                {
                    label: 'Moving Range',
                    data: selectedData.movingRanges,
                    borderColor: 'purple',
                    backgroundColor: 'purple',
                    pointRadius: 3,
                    fill: false,
                },
                {
                    label: 'MR-bar (CL)',
                    data: Array(selectedData.movingRanges.length).fill(selectedData.mrChart?.mrBar || null),
                    borderColor: 'green',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'UCL',
                    data: Array(selectedData.movingRanges.length).fill(selectedData.mrChart?.ucl || null),
                    borderColor: 'red',
                    borderDash: [5, 5],
                    pointRadius: 0,
                },
                {
                    label: 'Critical MR points',
                    data: selectedData.movingRanges.map((value) => (
                        Number(value) > Number(selectedData.mrChart?.ucl || 0) ? value : null
                    )),
                    borderColor: 'transparent',
                    backgroundColor: '#dc2626',
                    pointBorderColor: '#991b1b',
                    pointRadius: selectedData.movingRanges.map((value) => (
                        Number(value) > Number(selectedData.mrChart?.ucl || 0) ? 5 : 0
                    )),
                    pointHoverRadius: selectedData.movingRanges.map((value) => (
                        Number(value) > Number(selectedData.mrChart?.ucl || 0) ? 7 : 0
                    )),
                    pointBorderWidth: selectedData.movingRanges.map((value) => (
                        Number(value) > Number(selectedData.mrChart?.ucl || 0) ? 2 : 0
                    )),
                    showLine: false,
                    order: -1,
                },
            ],
        };
    }, [selectedData]);

    return (
        <div className={styles.controlChartContainer}>
            {!embedded ? <GoToDashboardButton /> : null}
            {loading ? (
                <div className={styles.loadingScreen}>
                    <div className={styles.spinner}></div>
                    <p>Loading...</p>
                </div>
            ) : (
                <div>
                    <header className={styles.pageHeader}>
                        <div>
                            <p className={styles.eyebrow}>Process stability</p>
                            <h2 className={styles.chartHeader}>Control Charts</h2>
                            <p className={styles.description}>
                                Track measurement drift, moving range behavior, and specification limits for each test step.
                            </p>
                        </div>
                    </header>

                    <div className={styles.filtersWrapper}>
                        <div className={styles.filtersRow}>
                            {/* Step Number Filter */}
                            <div className={styles.filterItem}>
                                <label htmlFor="stepNumberSelect" className={styles.filterLabel}>
                                    Step Number:
                                </label>
                                <select
                                    id="stepNumberSelect"
                                    value={pendingStepNumber}
                                    onChange={handleStepNumberChange}
                                    className={styles.selectDropdown}
                                    disabled={stepNumberLoading || stepNumbers.length === 0}
                                >
                                    <option value="">
                                        {stepNumberLoading
                                            ? 'Loading steps...'
                                            : stepNumbers.length > 0
                                                ? 'Select a step'
                                                : 'No numeric steps found'}
                                    </option>
                                    {stepNumbers.map((stepNumber) => (
                                        <option
                                            key={stepNumber.stepNumber}
                                            value={stepNumber.stepNumber}
                                        >
                                            Step {stepNumber.stepNumber}{stepNumber.stepName ? ` - ${stepNumber.stepName}` : ''}{stepNumber.count ? ` (${stepNumber.count})` : ''}
                                        </option>
                                    ))}
                                </select>
                                {pendingStepOption?.stepName && (
                                    <span className={styles.defaultValue}>{pendingStepOption.stepName}</span>
                                )}
                            </div>

                            {/* AteSwVersion Filter */}
                            <div className={styles.filterItem}>
                                <label htmlFor="ateSwVersionSelect" className={styles.filterLabel}>
                                    AteSwVersion:
                                </label>
                                <select
                                    id="ateSwVersionSelect"
                                    value={selectedAteSwVersion}
                                    onChange={(e) => setSelectedAteSwVersion(e.target.value)}
                                    className={styles.selectDropdown}
                                >
                                    <option value="">All Versions</option>
                                    {ateSwVersions.map((version) => (
                                        <option key={version.AteSwVersion} value={version.AteSwVersion}>
                                            {version.AteSwVersion} ({version.count})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Testspec Filter */}
                            <div className={styles.filterItem}>
                                <label htmlFor="testspecSelect" className={styles.filterLabel}>
                                    Testspec:
                                </label>
                                <select
                                    id="testspecSelect"
                                    value={selectedTestspec}
                                    onChange={(e) => setSelectedTestspec(e.target.value)}
                                    className={styles.selectDropdown}
                                >
                                    <option value="">All Testspecs</option>
                                    {testspecs.map((testspec) => (
                                        <option key={testspec.Testspec} value={testspec.Testspec}>
                                            {testspec.Testspec} ({testspec.count})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.filterItem}>
                                <label htmlFor="serialFilter" className={styles.filterLabel}>
                                    Serial Number:
                                </label>
                                <input
                                    id="serialFilter"
                                    type="text"
                                    value={serialFilter}
                                    onChange={(e) => setSerialFilter(e.target.value)}
                                    className={styles.input}
                                    placeholder="Exact serial number"
                                />
                            </div>

                            <div className={styles.filterItem}>
                                <label htmlFor="stationFilter" className={styles.filterLabel}>
                                    Station Name:
                                </label>
                                <input
                                    id="stationFilter"
                                    type="text"
                                    value={stationFilter}
                                    onChange={(e) => setStationFilter(e.target.value)}
                                    className={styles.input}
                                    placeholder="Exact station name"
                                />
                            </div>

                            <div className={styles.filterItem}>
                                <label htmlFor="filenameFilter" className={styles.filterLabel}>
                                    Filename:
                                </label>
                                <input
                                    id="filenameFilter"
                                    type="text"
                                    value={filenameFilter}
                                    onChange={(e) => setFilenameFilter(e.target.value)}
                                    className={styles.input}
                                    placeholder="Partial filename match"
                                />
                            </div>
                            <div className={styles.filterActions}>
                                <button
                                    onClick={handleApplyStepNumber}
                                    className={styles.applyButton}
                                    disabled={stepNumberLoading || !pendingStepNumber || chartLoading}
                                >
                                    {chartLoading ? 'Server is working...' : 'Apply Step'}
                                </button>
                            </div>
                        </div>

                        {/* 📅 Enhanced Date Filter */}
                        <div className={styles.filtersRow}>
                            <div className={styles.filterItem}>
                                <label className={styles.filterLabel}>Start Date:</label>
                                <DatePicker
                                    selected={tempStartDate}
                                    onChange={(date) => setTempStartDate(date)}
                                    placeholderText="Select Start Date"
                                    className={styles.dateInput}
                                    dateFormat="yyyy-MM-dd"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    showTimeSelect // optional — if you want time
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    timeCaption="Time"
                                />
                            </div>

                            <div className={styles.filterItem}>
                                <label className={styles.filterLabel}>End Date:</label>
                                <DatePicker
                                    selected={tempEndDate}
                                    onChange={(date) => setTempEndDate(date)}
                                    placeholderText="Select End Date"
                                    className={styles.dateInput}
                                    dateFormat="yyyy-MM-dd"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    showTimeSelect // optional
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    timeCaption="Time"
                                    minDate={tempStartDate}
                                />
                            </div>

                            {/* Quick Range Buttons */}
                            <div className={styles.quickRangeContainer}>
                                <button
                                    className={styles.quickRangeBtn}
                                    onClick={() => {
                                        const range = getQuickDateRange('today');
                                        setTempStartDate(range.start);
                                        setTempEndDate(range.end);
                                    }}
                                >
                                    Today
                                </button>

                                <button
                                    className={styles.quickRangeBtn}
                                    onClick={() => {
                                        const range = getQuickDateRange('last7');
                                        setTempStartDate(range.start);
                                        setTempEndDate(range.end);
                                    }}
                                >
                                    Last 7 Days
                                </button>

                                <button
                                    className={styles.quickRangeBtn}
                                    onClick={() => {
                                        const range = getQuickDateRange('thisMonth');
                                        setTempStartDate(range.start);
                                        setTempEndDate(range.end);
                                    }}
                                >
                                    This Month
                                </button>

                                <button
                                    className={styles.quickRangeBtn}
                                    onClick={() => {
                                        const range = getQuickDateRange('last30');
                                        setTempStartDate(range.start);
                                        setTempEndDate(range.end);
                                    }}
                                >
                                    Last 30 Days
                                </button>
                            </div>

                            <div className={styles.filterActions}>
                                <button onClick={handleApplyDateFilter} className={styles.applyButton}>
                                    Apply Date Filter
                                </button>
                                <button onClick={handleResetDateFilter} className={styles.resetButton}>
                                    Reset
                                </button>
                            </div>
                        </div>



                        <div className={styles.filtersRow}>
                            {/* LSL and USL Inputs */}
                            <div className={styles.calculatorContainer}>
                                <h3 className={styles.calculatorTitle}>Set Specification Limits</h3>

                                <div className={styles.filterRow}>
                                    <div className={styles.filterItem}>
                                        <label className={styles.filterLabel}>LSL:</label>
                                        <input
                                            type="number"
                                            value={tempLsl}
                                            onChange={(e) => setTempLsl(e.target.value)}
                                            className={styles.input}
                                        />
                                        {defaultLsl !== '' && (
                                            <span className={styles.defaultValue}>Default: {defaultLsl}</span>
                                        )}
                                    </div>

                                    <div className={styles.filterItem}>
                                        <label className={styles.filterLabel}>USL:</label>
                                        <input
                                            type="number"
                                            value={tempUsl}
                                            onChange={(e) => setTempUsl(e.target.value)}
                                            className={styles.input}
                                        />
                                        {defaultUsl !== '' && (
                                            <span className={styles.defaultValue}>Default: {defaultUsl}</span>
                                        )}
                                    </div>

                                    <div className={styles.filterItem}>
                                        <label className={styles.filterLabel}>Manual UCL:</label>
                                        <input
                                            type="number"
                                            value={tempUcl}
                                            onChange={(e) => setTempUcl(e.target.value)}
                                            className={styles.input}
                                        />
                                        {defaultUcl !== '' && (
                                            <span className={styles.defaultValue}>Default: {defaultUcl}</span>
                                        )}
                                    </div>

                                    <div className={styles.filterItem}>
                                        <label className={styles.filterLabel}>Manual LCL:</label>
                                        <input
                                            type="number"
                                            value={tempLcl}
                                            onChange={(e) => setTempLcl(e.target.value)}
                                            className={styles.input}
                                        />
                                        {defaultLcl !== '' && (
                                            <span className={styles.defaultValue}>Default: {defaultLcl}</span>
                                        )}
                                    </div>

                                </div>
                            </div>

                            <div className={styles.filterActions}>
                                <button
                                    onClick={handleApplyLimits}
                                    disabled={!!errorMessage}
                                    className={`${styles.button} ${errorMessage ? styles.disabledButton : ''}`}
                                >
                                    Apply Limits
                                </button>
                                <button onClick={handleResetLimits} className={styles.button}>
                                    Reset to Default
                                </button>
                            </div>
                        </div>
                        <SourceFileSummary
                            compact
                            startDate={startDate}
                            endDate={endDate}
                            ateSwVersion={selectedAteSwVersion}
                            testspec={selectedTestspec}
                            selectedFamilies={selectedFamilies}
                            onFamiliesChange={setSelectedFamilies}
                            title="Source files used for dashboard charts"
                        />
                        <section className={styles.calculatorSpotlight}>
                            <div>
                                <p className={styles.eyebrow}>Alternative baseline</p>
                                <h3 className={styles.calculatorSpotlightTitle}>Control Limits Calculator</h3>
                                <p className={styles.description}>
                                    Select a curated set of production files and rebuild the current step with alternate control limits when you want a tighter reference scope.
                                </p>
                            </div>
                            <button onClick={() => setIsCalculatorModalOpen(true)} className={styles.calculatorSpotlightButton}>
                                Open Control Limits Calculator
                            </button>
                        </section>
                        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
                    </div>


                    {chartLoading && (
                        <div className={styles.loadingScreen}>
                            <div className={styles.spinner}></div>
                            <p>Server is working to show control chart results...</p>
                        </div>
                    )}

                    {!chartLoading && selectedData && (
                        <section className={styles.selectedStepPanel}>
                            <div>
                                <p className={styles.eyebrow}>Selected step</p>
                                <h3 className={styles.selectedStepTitle}>
                                    Step {selectedData.stepNumber}{(selectedData.stepName || selectedStepOption?.stepName) ? ` - ${selectedData.stepName || selectedStepOption?.stepName}` : ''}
                                </h3>
                                <p className={styles.description}>
                                    Code considered: {selectedTestspec || 'all testspecs'}; ATE SW: {selectedAteSwVersion || 'all versions'}.
                                </p>
                            </div>

                            <div className={styles.metricGrid}>
                                <div className={styles.metricCard}>
                                    <span className={styles.metricLabel}>PASS-only count</span>
                                    <strong className={styles.metricValue}>{selectedData?.values?.length || 0}</strong>
                                </div>
                                <div className={styles.metricCard}>
                                    <span className={styles.metricLabel}>PASS + FAIL count</span>
                                    <strong className={styles.metricValue}>{selectedDataAll?.values?.length || 0}</strong>
                                </div>
                                <div className={styles.metricCard}>
                                    <span className={styles.metricLabel}>PASS-only mean</span>
                                    <strong className={styles.metricValue}>{selectedData?.individualChart?.xBar ?? 'N/A'}</strong>
                                </div>
                                <div className={styles.metricCard}>
                                    <span className={styles.metricLabel}>PASS + FAIL mean</span>
                                    <strong className={styles.metricValue}>{selectedDataAll?.individualChart?.xBar ?? 'N/A'}</strong>
                                </div>
                            </div>
                            <div className={styles.chartContainer}>

                                {/* Individual Chart */}
                                <div className={styles.chartBox} ref={individualChartRef} >
                                    <div className={styles.chartActionRow}>
                                        <button onClick={() => openModal('individual')} className={styles.enlargeButton}>Expand</button>
                                        <button onClick={() => toggleChartDetails('individual')} className={styles.enlargeButton}>
                                            {openChartDetails.individual ? 'Hide details' : 'Details'}
                                        </button>
                                        <label className={styles.exportCheckbox}>
                                            <input
                                                type="checkbox"
                                                checked={!!includeDetailsInExport.individual}
                                                onChange={() => toggleIncludeDetailsInExport('individual')}
                                            />
                                            Include details in PNG
                                        </label>
                                        <button
                                            onClick={() => exportChartAsPNG(individualChartExportRef, 'individual_chart')}
                                            className={styles.enlargeButton}
                                        >
                                            Export PNG
                                        </button>
                                    </div>
                                    <div ref={individualChartExportRef} data-report-capture-root="true">
                                        <h4 className={styles.chartTitle}>Individual Chart (PASS only)</h4>
                                        <Line
                                            data={passOnlyChartData}
                                            options={{
                                                responsive: true,
                                                plugins: {
                                                    legend: {
                                                        position: 'top',
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Individual Chart',
                                                    },
                                                },
                                                scales: {
                                                    y: {
                                                        title: {
                                                            display: true,
                                                            text: 'Measure Value',
                                                        },
                                                    },
                                                    x: {
                                                        title: {
                                                            display: true,
                                                            text: 'Serial Number',
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {(openChartDetails.individual || includeDetailsInExport.individual) && (
                                            <div className={styles.chartMetricPanel}>
                                                <div className={styles.chartMetricGrid}>
                                                    {passOnlyDetailCards.map((metric) => (
                                                        <div key={`individual-${metric.label}`} className={styles.chartMetricCard}>
                                                            <span>{metric.label}</span>
                                                            <strong>{metric.value}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                    {/* Individual Chart (PASS + FAIL) */}
                                    {selectedDataAll && (
                                        <div className={styles.chartBox} ref={individualAllChartRef}>
                                            <div className={styles.chartActionRow}>
                                                <button onClick={() => openModal('individualAll')} className={styles.enlargeButton}>
                                                    Expand
                                                </button>
                                                <button onClick={() => toggleChartDetails('individualAll')} className={styles.enlargeButton}>
                                                    {openChartDetails.individualAll ? 'Hide details' : 'Details'}
                                                </button>
                                                <label className={styles.exportCheckbox}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!includeDetailsInExport.individualAll}
                                                        onChange={() => toggleIncludeDetailsInExport('individualAll')}
                                                    />
                                                    Include details in PNG
                                                </label>
                                                <button onClick={() => exportChartAsPNG(individualAllChartExportRef, 'individual_chart_pass_fail')} className={styles.enlargeButton}>
                                                    Export PNG
                                                </button>
                                            </div>
                                            <div ref={individualAllChartExportRef}>
                                                <h4 className={styles.chartTitle}>Individual Chart (PASS + FAIL)</h4>
                                                <Line
                                                    data={passFailChartData}
                                                    options={{
                                                        responsive: true,
                                                        plugins: {
                                                            legend: {
                                                                position: 'top',
                                                            },
                                                            title: {
                                                                display: true,
                                                                text: 'Individual Chart (PASS + FAIL)',
                                                            },
                                                        },
                                                        scales: {
                                                            y: {
                                                                title: {
                                                                    display: true,
                                                                    text: 'Measure Value',
                                                                },
                                                            },
                                                            x: {
                                                                title: {
                                                                    display: true,
                                                                    text: 'Serial Number',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {(openChartDetails.individualAll || includeDetailsInExport.individualAll) && (
                                                    <div className={styles.chartMetricPanel}>
                                                        <div className={styles.chartMetricGrid}>
                                                            {passFailDetailCards.map((metric) => (
                                                                <div key={`individual-all-${metric.label}`} className={styles.chartMetricCard}>
                                                                    <span>{metric.label}</span>
                                                                    <strong>{metric.value}</strong>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                {/* Normal Distribution Chart */}
                                {normalDistributionChartData.datasets.length > 0 ? (
                                    <div className={styles.chartBox} ref={normalChartRef}>
                                        <div className={styles.chartActionRow}>
                                            <button onClick={() => openModal('normal')} className={styles.enlargeButton}>
                                                Expand
                                            </button>
                                            <button onClick={() => toggleChartDetails('normal')} className={styles.enlargeButton}>
                                                {openChartDetails.normal ? 'Hide details' : 'Details'}
                                            </button>
                                            <label className={styles.exportCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!includeDetailsInExport.normal}
                                                    onChange={() => toggleIncludeDetailsInExport('normal')}
                                                />
                                                Include details in PNG
                                            </label>
                                            <button onClick={() => exportChartAsPNG(normalChartExportRef, 'normal_distribution_chart')} className={styles.enlargeButton}>
                                                Export PNG
                                            </button>
                                        </div>

                                        <div ref={normalChartExportRef}>
                                            <h4 className={styles.chartTitle}>Normal Distribution Chart</h4>
                                            <Chart type="bar" data={normalDistributionChartData} options={normalDistributionChartOptions} />
                                            {(openChartDetails.normal || includeDetailsInExport.normal) && (
                                                <div className={styles.chartMetricPanel}>
                                                    <div className={styles.chartMetricGrid}>
                                                        {normalDetailCards.map((metric) => (
                                                            <div key={`normal-${metric.label}`} className={styles.chartMetricCard}>
                                                                <span>{metric.label}</span>
                                                                <strong>{metric.value}</strong>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.chartBox}>
                                        <p className={styles.noDataMessage}>No Normal Distribution Data Available</p>
                                    </div>
                                )}

                                {/* MR Chart */}
                                <div className={styles.chartBox} ref={mrChartRef}>
                                    <div className={styles.chartActionRow}>
                                        <button onClick={() => openModal('mr')} className={styles.enlargeButton}>Expand</button>
                                        <button onClick={() => toggleChartDetails('mr')} className={styles.enlargeButton}>
                                            {openChartDetails.mr ? 'Hide details' : 'Details'}
                                        </button>
                                        <label className={styles.exportCheckbox}>
                                            <input
                                                type="checkbox"
                                                checked={!!includeDetailsInExport.mr}
                                                onChange={() => toggleIncludeDetailsInExport('mr')}
                                            />
                                            Include details in PNG
                                        </label>
                                        <button onClick={() => exportChartAsPNG(mrChartExportRef, 'mr_chart')} className={styles.enlargeButton}>Export PNG</button>
                                    </div>
                                    <div ref={mrChartExportRef}>
                                        <h4 className={styles.chartTitle}>MR Chart</h4>
                                        <Line
                                            data={mrChartData}
                                            options={{
                                                responsive: true,
                                                plugins: {
                                                    legend: {
                                                        position: 'top',
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Moving Range (MR) Chart',
                                                    },
                                                },
                                                scales: {
                                                    y: {
                                                        title: {
                                                            display: true,
                                                            text: 'Moving Range',
                                                        },
                                                    },
                                                    x: {
                                                        title: {
                                                            display: true,
                                                            text: 'Serial Number',
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {(openChartDetails.mr || includeDetailsInExport.mr) && (
                                            <div className={styles.chartMetricPanel}>
                                                <div className={styles.chartMetricGrid}>
                                                    {mrDetailCards.map((metric) => (
                                                        <div key={`mr-${metric.label}`} className={styles.chartMetricCard}>
                                                            <span>{metric.label}</span>
                                                            <strong>{metric.value}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <Modal
                                isOpen={isModalOpen}
                                onRequestClose={closeModal}
                                contentLabel="Enlarged Chart"
                                className={`${styles.modalContent} ${modalSizeClass}`}
                                overlayClassName={styles.modalOverlay}
                            >
                                <div className={styles.modalToolbar}>
                                    <div className={styles.buttonGroup}>
                                        {CONTROL_MODAL_SIZES.map((size) => (
                                            <button
                                                key={size.key}
                                                type="button"
                                                onClick={() => setModalSize(size.key)}
                                                className={modalSize === size.key ? styles.applyButton : styles.resetButton}
                                            >
                                                {size.label}
                                            </button>
                                        ))}
                                    </div>
                                    <button onClick={closeModal} className={styles.closeButton}>Close</button>
                                </div>
                                <p className={styles.sectionDescription}>Use the size buttons to inspect the chart with a larger plotting area.</p>

                                {modalContent === 'individual' && (
                                    <div className={styles.modalChartSection}>
                                        <h4>Individual Chart (expanded)</h4>
                                        <div className={`${styles.modalChartStage} ${modalChartStageClass}`}>
                                            <Line
                                                data={passOnlyChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: { legend: { position: 'top' } },
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                    {modalContent === 'individualAll' && selectedDataAll && (
                                        <div className={styles.modalChartSection}>
                                            <h4>Individual Chart (PASS + FAIL) - expanded</h4>
                                            <div className={`${styles.modalChartStage} ${modalChartStageClass}`}>
                                                <Line
                                                    data={passFailChartData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            legend: { position: 'top' },
                                                            title: { display: true, text: 'Individual Chart (PASS + FAIL)' },
                                                        },
                                                        scales: {
                                                            y: {
                                                                title: { display: true, text: 'Measure Value' },
                                                            },
                                                            x: {
                                                                title: { display: true, text: 'Serial Number' },
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                {modalContent === 'mr' && (
                                    <div className={styles.modalChartSection}>
                                        <h4>MR Chart (expanded)</h4>
                                        <div className={`${styles.modalChartStage} ${modalChartStageClass}`}>
                                            <Line
                                                data={mrChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { position: 'top' },
                                                        title: { display: true, text: 'Moving Range (MR) Chart' },
                                                    },
                                                    scales: {
                                                        y: {
                                                            title: { display: true, text: 'Moving Range' },
                                                        },
                                                        x: {
                                                            title: { display: true, text: 'Serial Number' },
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {modalContent === 'normal' && (
                                    <div className={styles.modalChartSection}>
                                        <h4>Normal Distribution Chart (expanded)</h4>
                                        <div className={`${styles.modalChartStage} ${modalChartStageClass}`}>
                                            <Chart type="bar" data={normalDistributionChartData} options={{ ...normalDistributionChartOptions, maintainAspectRatio: false }} />
                                        </div>
                                    </div>
                                )}
                            </Modal>

                            <Modal
                                isOpen={isCalculatorModalOpen}
                                onRequestClose={() => {
                                    setErrorMessage(''); // Clear error message when modal closes
                                    setIsCalculatorModalOpen(false);
                                }}
                                className={styles.modal2}
                                overlayClassName={styles.overlay2}
                            >
                                <h2 className={styles.modalTitle}>UCL/LCL Calculator</h2>
                                <div className={styles.calculatorSummary}>
                                    <label className={styles.label}>Step Number:</label>
                                    <p className={styles.text}>{selectedStepNumber || "No step number selected"}</p>
                                </div>
                                <div className={styles.fileList}>
                                    <div className={styles.sortContainer}>
                                        <label className={styles.label}>Select Files:</label>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'))}
                                        >
                                            Sort by: {sortOrder === 'newest' ? 'Oldest' : 'Newest'}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.resetButton}
                                            onClick={() => {
                                                setCalculatorSearch('');
                                                setCalculatorStatusFilter('');
                                                setCalculatorAteFilter('');
                                                setCalculatorTestspecFilter('');
                                                setCalculatorDateFrom('');
                                                setCalculatorDateTo('');
                                            }}
                                        >
                                            Clear filters
                                        </button>
                                    </div>
                                    <div className={styles.calculatorFilterGrid}>
                                        <div className={styles.filterItem}>
                                            <label className={styles.label} htmlFor="calculatorSearch">Search</label>
                                            <input
                                                id="calculatorSearch"
                                                type="text"
                                                className={styles.filterInput}
                                                placeholder="Filename, serial, version, testspec"
                                                value={calculatorSearch}
                                                onChange={(event) => setCalculatorSearch(event.target.value)}
                                            />
                                        </div>
                                        <div className={styles.filterItem}>
                                            <label className={styles.label} htmlFor="calculatorStatusFilter">UUT Status</label>
                                            <select
                                                id="calculatorStatusFilter"
                                                className={styles.selectDropdown}
                                                value={calculatorStatusFilter}
                                                onChange={(event) => setCalculatorStatusFilter(event.target.value)}
                                            >
                                                <option value="">All statuses</option>
                                                {calculatorStatuses.map((status) => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={styles.filterItem}>
                                            <label className={styles.label} htmlFor="calculatorAteFilter">ATE SW Version</label>
                                            <select
                                                id="calculatorAteFilter"
                                                className={styles.selectDropdown}
                                                value={calculatorAteFilter}
                                                onChange={(event) => setCalculatorAteFilter(event.target.value)}
                                            >
                                                <option value="">All versions</option>
                                                {calculatorAteVersions.map((version) => (
                                                    <option key={version} value={version}>{version}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={styles.filterItem}>
                                            <label className={styles.label} htmlFor="calculatorTestspecFilter">Testspec</label>
                                            <select
                                                id="calculatorTestspecFilter"
                                                className={styles.selectDropdown}
                                                value={calculatorTestspecFilter}
                                                onChange={(event) => setCalculatorTestspecFilter(event.target.value)}
                                            >
                                                <option value="">All testspecs</option>
                                                {calculatorTestspecs.map((testspec) => (
                                                    <option key={testspec} value={testspec}>{testspec}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={styles.filterItem}>
                                            <label className={styles.label} htmlFor="calculatorDateFrom">Start Date</label>
                                            <input
                                                id="calculatorDateFrom"
                                                type="date"
                                                className={styles.dateInput}
                                                value={calculatorDateFrom}
                                                onChange={(event) => setCalculatorDateFrom(event.target.value)}
                                            />
                                        </div>
                                        <div className={styles.filterItem}>
                                            <label className={styles.label} htmlFor="calculatorDateTo">End Date</label>
                                            <input
                                                id="calculatorDateTo"
                                                type="date"
                                                className={styles.dateInput}
                                                value={calculatorDateTo}
                                                onChange={(event) => setCalculatorDateTo(event.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.calculatorSelectionSummary}>
                                        <span>{visibleCalculatorFiles.length} visible files</span>
                                        <span>{selectedFileIds.length} selected</span>
                                    </div>
                                    <div className={styles.fileTable}>
                                        {visibleCalculatorFiles.map((file, index) => (
                                            <div key={file.id || `${file.filename}-${index}`} className={styles.fileRow}>
                                                <input
                                                    type="checkbox"
                                                    id={`file-${index}`}
                                                    value={file.id}
                                                    checked={selectedFileIds.includes(file.id)}
                                                    onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        const fileId = file.id;
                                                        if (isChecked) {
                                                            setSelectedFileIds((prev) => [...new Set([...prev, fileId])]);
                                                        } else {
                                                            setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));
                                                        }
                                                    }}
                                                    className={styles.checkbox}
                                                />
                                                <label htmlFor={`file-${index}`} className={styles.fileLabel}>
                                                    <span className={styles.fileName}>{file.filename}</span>
                                                    <span className={styles.fileDetail}>Serial Number: {file.serialNumber}</span>
                                                    <span className={styles.fileDetail}>Test Started: {file.testStarted}</span>
                                                    <span className={styles.fileDetail}>AteSwVersion: {file.AteSwVersion}</span>
                                                    <span className={styles.fileDetail}>Testspec: {file.Testspec || '-'}</span>
                                                    <span className={styles.fileDetail}>UUT Status: {file.uutStatus}</span>
                                                </label>
                                            </div>
                                        ))}
                                        {!visibleCalculatorFiles.length && (
                                            <div className={styles.emptyCalculatorState}>No files match the current search and filters.</div>
                                        )}
                                    </div>
                                </div>

                                {/* Error Message Section */}
                                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

                                <div className={styles.modalActions}>
                                    <button
                                        onClick={async () => {
                                            try {
                                                if (!selectedStepNumber) {
                                                    setErrorMessage('No step number selected. Please select a step number.');
                                                    return;
                                                }

                                                if (!selectedFileIds.length) {
                                                    setErrorMessage('No files selected. Please select at least one file.');
                                                    return;
                                                }

                                                const response = await calculateUclLcl({
                                                    selectedFileIds,
                                                    stepNumber: selectedStepNumber,
                                                });

                                                if (response?.results) {
                                                    persistControlChartResults({
                                                        mode: 'production',
                                                        results: response.results,
                                                    });
                                                    navigate('/new-control-chart', { state: { results: response.results } });
                                                } else {
                                                    setErrorMessage('No data returned from the backend for the selected test.');
                                                }
                                            } catch (error) {
                                                setErrorMessage(error.response?.data?.error || error.response?.data?.message || 'An error occurred during the calculation.');
                                                console.error('Error calculating UCL and LCL:', error);
                                            }
                                        }}
                                        className={styles.confirmButton}
                                    >
                                        Calculate Limits
                                    </button>
                                    <button onClick={() => {
                                        setErrorMessage(''); // Clear error message when modal closes
                                        setIsCalculatorModalOpen(false);
                                    }} className={styles.closeButton}>
                                        Close
                                    </button>
                                </div>
                            </Modal>



                            <div className={styles.exportButtons}>
                                <button onClick={exportTableAsCSV} className={styles.applyButton}>Export Table as CSV</button>
                                <button onClick={exportTableAsPDF} className={styles.applyButton}>Export Table as PDF</button>
                                <button onClick={() => setShowTable(!showTable)} className={styles.applyButton}>
                                    {showTable ? 'Hide Table' : 'Show Table'}
                                </button>
                            </div>

                            {/* Display extracted values and serial numbers in a table below the chart */}
                            {showTable && (
                                <div className={styles.tableContainer}>
                                    <h4 className={styles.tableHeader}>Underlying detail rows for Step {selectedData.stepNumber}</h4>
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                {tableColumns.map((column) => (
                                                    <th key={column.key}>
                                                        <button
                                                            type="button"
                                                            className={styles.tableSortButton}
                                                            onClick={() => toggleTableSort(column.key)}
                                                        >
                                                            <span>{column.label}</span>
                                                            <span className={styles.tableSortIndicator}>
                                                                {tableSort.key === column.key
                                                                    ? (tableSort.direction === 'asc' ? '↑' : '↓')
                                                                    : '↕'}
                                                            </span>
                                                        </button>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableRows.map((row, index) => (
                                                <tr
                                                    key={`${row.serialNumber || 'row'}-${index}`}
                                                    className={
                                                        row.isCritical
                                                            ? styles.tableRowCritical
                                                            : row.isWarning
                                                                ? styles.tableRowWarning
                                                                : ''
                                                    }
                                                >
                                                    <td>{index + 1}</td>
                                                    {tableColumns.map((column) => (
                                                        <td key={column.key}>
                                                            {column.key === 'criticalityLabel' ? (
                                                                <span
                                                                    className={
                                                                        row.isCritical
                                                                            ? styles.criticalBadge
                                                                            : row.isWarning
                                                                                ? styles.warningBadge
                                                                                : styles.stableBadge
                                                                    }
                                                                >
                                                                    {row[column.key]}
                                                                </span>
                                                            ) : typeof column.format === 'function' ? (
                                                                column.format(row[column.key])
                                                            ) : (
                                                                row[column.key] ?? 'N/A'
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        </section>
                    )}
                    <button
                        className={styles.helpButton}
                        onClick={openHelp}
                        aria-label="Help"
                    >
                        ?
                    </button>
                    <Modal
                        isOpen={isHelpOpen}
                        onRequestClose={closeHelp}
                        className={styles.helpModalContent}
                        overlayClassName={styles.helpModalOverlay}
                        contentLabel="Help Information"
                    >
                        <button onClick={closeHelp} className={styles.closeHelpButton}>
                            Close
                        </button>
                        <div className={styles.helpText}>
                            <h2>About this Page</h2>
                            <p>
                                This page provides a comprehensive analysis of process stability and capability for a selected numeric production step:
                            </p>
                            <ul>
                                <li>
                                    <strong>Individual Chart:</strong> Shows each measurement with the center line, control limits, specification limits, and critical-point highlighting.
                                </li>
                                <li>
                                    <strong>PASS + FAIL View:</strong> Lets you compare the same step using the wider population instead of PASS-only data.
                                </li>
                                <li>
                                    <strong>Normal Distribution and MR Charts:</strong> Help you inspect spread, distribution shape, and point-to-point variation.
                                </li>
                                <li>
                                    <strong>Detailed Table:</strong> Provides sortable row-level detail with criticality, reasons, timestamps, limits, and file context.
                                </li>
                                <li>
                                    <strong>Filters:</strong> Customize the analysis with:
                                    <ul>
                                        <li><strong>Date Range:</strong> Focus on a specific time period.</li>
                                        <li><strong>ATE SW Version:</strong> Analyze data by software version.</li>
                                        <li><strong>Testspec and source families:</strong> Narrow the production scope.</li>
                                        <li><strong>Serial number and station name:</strong> Focus on one exact serial or one exact station.</li>
                                        <li><strong>Filename:</strong> Filter by partial source filename match.</li>
                                        <li><strong>Specification Limits (USL, LSL):</strong> Override specification limits manually.</li>
                                        <li><strong>Control Limits (UCL, LCL):</strong> Override displayed control limits manually.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Calculator Features:</strong>
                                    <ul>
                                        <li><strong>Manual limit overrides:</strong> Instantly update chart overlays and criticality logic.</li>
                                        <li><strong>Control Limits Calculator:</strong> Calculate alternate control limits from selected files when you need a narrower reference set.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Export Options:</strong> Export charts as PNG, and export the enriched detail table as CSV or PDF.
                                </li>
                            </ul>
                            <p>
                                Use this tool to monitor drift, spot outliers and near-limit behavior, and assess whether the process is both stable and capable.
                            </p>
                        </div>
                    </Modal>


                </div>
            )}
        </div>
    );
};

export default ControlChart;
